import React from 'react'
import { cn } from '../UI/cn'

interface PillTabItem<K extends string> {
  key: K
  title: React.ReactNode
}

interface PillTabsProps<K extends string> {
  tabs: ReadonlyArray<PillTabItem<K>>
  activeKey: NoInfer<K>
  onChange: (key: NoInfer<K>) => void
  /** Unique-per-page prefix for tab/panel id wiring. Pair it with the
   *  matching `<TabPanel idPrefix={...}>` so screen readers can navigate
   *  from a tab to the panel it controls. */
  idPrefix: string
  /** Optional extra classes on the tablist container (e.g. `mt-12`, `w-fit`). */
  className?: string
  /** Accessible name for the tablist; required for screen readers when the
   *  surrounding heading doesn't already label this control. */
  'aria-label'?: string
  /** `sm` is the compact variant (matches the DS2026 `sm` controls — height
   *  aligns with `size="sm"` buttons/dropdowns); `md` (default) matches the
   *  homepage chrome; `lg` scales up for primary surfaces like the Targets
   *  page; `xl` adds another step for surfaces with extra space (e.g. the
   *  Contacts page header). */
  size?: 'sm' | 'md' | 'lg' | 'xl'
}

const tabIdFor = (prefix: string, key: string): string => `${prefix}-tab-${key}`
const panelIdFor = (prefix: string, key: string): string =>
  `${prefix}-panel-${key}`

/**
 * Pill-style segmented tabs with `role="tablist"` / `role="tab"` /
 * `aria-selected` + `aria-controls` wiring, roving tabindex, and
 * Arrow/Home/End focus management, matching the WAI-ARIA Authoring
 * Practices tabs pattern.
 *
 * Pair with `<TabPanel>` using the same `idPrefix` so each tab points to
 * its panel via `aria-controls` and each panel back-references its tab
 * via `aria-labelledby`.
 */
export function PillTabs<K extends string>({
  tabs,
  activeKey,
  onChange,
  idPrefix,
  className,
  'aria-label': ariaLabel,
  size = 'md',
}: PillTabsProps<K>) {
  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    let nextIndex: number | null = null
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length
    else if (event.key === 'ArrowLeft')
      nextIndex = (index - 1 + tabs.length) % tabs.length
    else if (event.key === 'Home') nextIndex = 0
    else if (event.key === 'End') nextIndex = tabs.length - 1
    if (nextIndex === null) return
    event.preventDefault()
    const nextTab = tabs[nextIndex]
    onChange(nextTab.key)
    const container = event.currentTarget.parentElement
    container
      ?.querySelectorAll<HTMLButtonElement>('[role="tab"]')
      [nextIndex]?.focus()
  }

  const containerClass =
    size === 'xl'
      ? 'h-12 p-1 rounded-lg gap-1'
      : size === 'lg'
        ? 'h-11 p-1 rounded-lg gap-0.5 xl:gap-1'
        : size === 'sm'
          ? 'h-8 p-0.5 rounded-lg gap-0.5'
          : 'h-11 p-1 rounded-lg gap-1'
  // size=lg compresses padding below the xl breakpoint (1280px) so the tabs
  // tighten up before they'd start to overflow the container chrome. Text
  // stays at text-base for lg, bumps to text-lg for xl. size=sm is the compact
  // variant whose container height matches the DS2026 `sm` buttons.
  const buttonClass =
    size === 'xl'
      ? 'h-10 px-5 text-lg'
      : size === 'lg'
        ? 'h-9 px-3 xl:px-5 text-base'
        : size === 'sm'
          ? 'h-7 px-2.5 text-sm'
          : 'h-9 px-3 text-sm'

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'flex bg-neutral-100 items-center',
        containerClass,
        className
      )}
    >
      {tabs.map((tab, index) => {
        const isActive = activeKey === tab.key
        return (
          <button
            key={tab.key}
            id={tabIdFor(idPrefix, tab.key)}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={panelIdFor(idPrefix, tab.key)}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(tab.key)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={cn(
              'flex items-center justify-center font-medium tracking-[-0.2px] whitespace-nowrap rounded-md transition-colors',
              buttonClass,
              isActive
                ? 'bg-white text-neutral-800 shadow-tab-active'
                : 'text-neutral-600 hover:text-neutral-800'
            )}
          >
            {tab.title}
          </button>
        )
      })}
    </div>
  )
}

interface TabPanelProps<K extends string> {
  /** Must match the `idPrefix` on the corresponding `<PillTabs>`. */
  idPrefix: string
  /** This panel's tab key. */
  tabKey: K
  /** Active tab key (from the same state as the PillTabs `activeKey`). */
  activeKey: NoInfer<K>
  className?: string
  children: React.ReactNode
}

/**
 * Tabpanel companion to `<PillTabs>`. Wires `role="tabpanel"`,
 * `id`/`aria-labelledby` back to the matching tab, and hides the
 * region from assistive tech when its tab is not active.
 */
export function TabPanel<K extends string>({
  idPrefix,
  tabKey,
  activeKey,
  className,
  children,
}: TabPanelProps<K>) {
  const isActive = activeKey === tabKey
  return (
    <div
      role="tabpanel"
      id={panelIdFor(idPrefix, tabKey)}
      aria-labelledby={tabIdFor(idPrefix, tabKey)}
      tabIndex={0}
      hidden={!isActive}
      className={className}
    >
      {isActive && children}
    </div>
  )
}
